import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLeads } from '../../context/LeadsContext';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { normalizePhone } from '../../utils/phoneUtils';
import { db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getLatestQuoteForConversation } from '../../utils/quoteUtils';
import PropertyDetailsEditor, { EMPTY_PROPERTY_DETAILS } from '../propertyDetails/PropertyDetailsEditor';

// Strip empties so the lead doc isn't littered with blank strings.
// Returns null if every field is empty/false, so we don't persist an empty obj.
function sanitizePropertyDetails(pd) {
  if (!pd) return null;
  const cleaned = {
    flashNote:         (pd.flashNote || '').trim().slice(0, 120),
    gateCode:          (pd.gateCode || '').trim(),
    parkingNotes:      (pd.parkingNotes || '').trim(),
    entryInstructions: (pd.entryInstructions || '').trim(),
    petOnSite:         !!pd.petOnSite,
    petNotes:          (pd.petNotes || '').trim(),
    alarmInfo:         (pd.alarmInfo || '').trim(),
    siteHazards:       (pd.siteHazards || '').trim(),
  };
  const hasAny =
    cleaned.flashNote ||
    cleaned.gateCode ||
    cleaned.parkingNotes ||
    cleaned.entryInstructions ||
    cleaned.petOnSite ||
    cleaned.petNotes ||
    cleaned.alarmInfo ||
    cleaned.siteHazards;
  return hasAny ? cleaned : null;
}

const NewLeadModal = ({ isOpen, onClose, mode = 'create', lead = null }) => {  const { addLead, updateLead } = useLeads();
  const { profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetNumber: '',
    streetName: '',
    city: '',
    state: '',
    zip: '',
    service: '',
    leadSource: '',
    notes: '',
    propertyDetails: { ...EMPTY_PROPERTY_DETAILS },
  });
  const [linkLatestQuote, setLinkLatestQuote] = useState(false);
  const [fromConversationId, setFromConversationId] = useState(null);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      streetNumber: '',
      streetName: '',
      city: '',
      state: '',
      zip: '',
      service: '',
      leadSource: '',
      notes: '',
      propertyDetails: { ...EMPTY_PROPERTY_DETAILS },
    });
    setError('');
    setLinkLatestQuote(false);
    setFromConversationId(null);
  };

  // Initialize form when opening in create/edit mode
  useEffect(() => {
    if (!isOpen) return;

    // When opened from Inbox, we may receive context for linking latest quote
    if (mode === 'create' && lead && lead.fromConversationId) {
      setFromConversationId(lead.fromConversationId);
      setLinkLatestQuote(!!lead.hasQuote);
    }

    if (mode === 'edit' && lead) {
      const name = (lead.name || '').trim();
      const parts = name.split(' ').filter(Boolean);
      const derivedFirstName = lead.firstName || parts[0] || '';
      const derivedLastName = lead.lastName || parts.slice(1).join(' ') || '';

      // Handle address schema: support both new (streetNumber/streetName) and old (line1) formats
      let streetNumber = lead.address?.streetNumber || '';
      let streetName = lead.address?.streetName || '';
      
      // Fallback: if no streetNumber/streetName but we have line1, try to parse it
      if (!streetNumber && !streetName && lead.address?.line1) {
        const line1 = lead.address.line1;
        const lineParts = line1.split(' ', 1);
        if (lineParts.length >= 1 && lineParts[0].replace('-', '').match(/^\d+$/)) {
          streetNumber = lineParts[0];
          streetName = line1.substring(lineParts[0].length).trim();
        } else {
          // Couldn't parse, put whole thing in streetName
          streetName = line1;
        }
      }

      setFormData({
        firstName: derivedFirstName,
        lastName: derivedLastName,
        email: lead.email || '',
        phone: lead.phone || '',
        streetNumber: streetNumber,
        streetName: streetName,
        city: lead.address?.city || '',
        state: lead.address?.state || '',
        zip: lead.address?.zip || '',
        service: lead.service || '',
        leadSource: lead.leadSource || '',
        notes: lead.notes || '',
        propertyDetails: { ...EMPTY_PROPERTY_DETAILS, ...(lead.propertyDetails || {}) },
      });
      setError('');
      setIsSubmitting(false);
    } else if (mode === 'create') {
      resetForm();
      setIsSubmitting(false);
    }
  }, [isOpen, mode, lead]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (mode === 'edit' && !lead) {
      setError('No lead selected for editing');
      return;
    }

    setIsSubmitting(true);

    try {
      // Normalize phone number before saving
      const cleanedPhone = normalizePhone(formData.phone);
      
      // Build line1 from streetNumber + streetName for backwards compatibility
      const streetNumber = formData.streetNumber.trim();
      const streetName = formData.streetName.trim();
      const line1 = [streetNumber, streetName].filter(Boolean).join(' ') || null;
      
      const basePayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim() || null,
        phone: cleanedPhone || null,
        address: {
          line1: line1,
          streetNumber: streetNumber || null,
          streetName: streetName || null,
          city: formData.city.trim() || null,
          state: formData.state.trim() || null,
          zip: formData.zip.trim() || null,
        },
        service: formData.service || null,
        leadSource: formData.leadSource || null,
        notes: formData.notes.trim() || null,
        propertyDetails: sanitizePropertyDetails(formData.propertyDetails),
      };

      if (mode === 'create') {
        // Create lead object with default stage/location
        const newLead = {
          ...basePayload,
          stage: 'New',
          locationId: profile?.locationId,
        };
        const newLeadId = await addLead(newLead);

        // Optional: link latest quote from the originating conversation
        if (fromConversationId && linkLatestQuote) {
          try {
            const latestQuoteId = await getLatestQuoteForConversation(fromConversationId);
            if (latestQuoteId) {
              await updateDoc(doc(db, 'quotes', latestQuoteId), { leadId: newLeadId });
            }
          } catch (e) {
            console.error('Failed to link latest quote to new lead', e);
          }
        }
      } else {
        // Edit existing lead: do not override stage/location/timestamps here
        await updateLead(lead.id, basePayload);
      }

      // Success - close modal and reset form
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error saving lead:', err);
      setError(err.message || 'Failed to save lead. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  if (!isOpen) return null;

  const isEditMode = mode === 'edit';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold" style={{ color: 'var(--cb-navy)' }}>
              {isEditMode ? 'Edit Lead' : 'New Lead'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              disabled={isSubmitting}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" style={{ color: 'var(--cb-navy)' }}>
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" style={{ color: 'var(--cb-navy)' }}>
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" style={{ color: 'var(--cb-navy)' }}>
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div>
                  <Label htmlFor="phone" style={{ color: 'var(--cb-navy)' }}>
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              {/* Address Fields */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <h3 className="font-semibold" style={{ color: 'var(--cb-navy)' }}>
                  Address
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="streetNumber" style={{ color: 'var(--cb-navy)' }}>
                      Street Number
                    </Label>
                    <Input
                      id="streetNumber"
                      value={formData.streetNumber}
                      onChange={(e) => handleChange('streetNumber', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <Label htmlFor="streetName" style={{ color: 'var(--cb-navy)' }}>
                      Street Name
                    </Label>
                    <Input
                      id="streetName"
                      value={formData.streetName}
                      onChange={(e) => handleChange('streetName', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city" style={{ color: 'var(--cb-navy)' }}>
                      City
                    </Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" style={{ color: 'var(--cb-navy)' }}>
                      State
                    </Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      disabled={isSubmitting}
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip" style={{ color: 'var(--cb-navy)' }}>
                      Zip Code
                    </Label>
                    <Input
                      id="zip"
                      value={formData.zip}
                      onChange={(e) => handleChange('zip', e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Service & Lead Source */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <Label htmlFor="service" style={{ color: 'var(--cb-navy)' }}>
                    Service
                  </Label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => handleChange('service', value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="service">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Roofing">Roofing</SelectItem>
                      <SelectItem value="Siding">Siding</SelectItem>
                      <SelectItem value="Gutters">Gutters</SelectItem>
                      <SelectItem value="Deck / Patio">Deck / Patio</SelectItem>
                      <SelectItem value="Walkway / Driveway">Walkway / Driveway</SelectItem>
                      <SelectItem value="Fence Install">Fence Install</SelectItem>
                      <SelectItem value="Tree Service">Tree Service</SelectItem>
                      <SelectItem value="Pest Control">Pest Control</SelectItem>
                      <SelectItem value="Soft Wash">Soft Wash</SelectItem>
                      <SelectItem value="Pressure Wash">Pressure Wash</SelectItem>
                      <SelectItem value="Window Cleaning">Window Cleaning</SelectItem>
                      <SelectItem value="Junk Removal">Junk Removal</SelectItem>
                      <SelectItem value="Custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="leadSource" style={{ color: 'var(--cb-navy)' }}>
                    Lead Source
                  </Label>
                  <Select
                    value={formData.leadSource}
                    onValueChange={(value) => handleChange('leadSource', value)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="leadSource">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Website">Website</SelectItem>
            {fromConversationId && (
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={linkLatestQuote}
                  onChange={(e) => setLinkLatestQuote(e.target.checked)}
                  className="h-3 w-3 rounded border-gray-300"
                />
                <span>Also link the latest quote from this conversation to this lead</span>
              </label>
            )}

                      <SelectItem value="Referral">Referral</SelectItem>
                      <SelectItem value="Google">Google</SelectItem>
                      <SelectItem value="Facebook">Facebook</SelectItem>
                      <SelectItem value="Door Hanger">Door Hanger</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Property & Access — operational facts surfaced everywhere this lead appears */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="font-semibold mb-2" style={{ color: 'var(--cb-navy)' }}>
                  Property &amp; Access <span className="text-xs text-gray-400 font-normal">(optional)</span>
                </h3>
                <PropertyDetailsEditor
                  value={formData.propertyDetails}
                  onChange={(next) => handleChange('propertyDetails', next)}
                />
              </div>

              {/* Notes */}
              <div className="pt-4 border-t border-gray-200">
                <Label htmlFor="notes" style={{ color: 'var(--cb-navy)' }}>
                  Notes
                </Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--cb-orange)] focus:border-transparent resize-none"
                  placeholder="Add any additional notes..."
                />
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </span>
              ) : (
                isEditMode ? 'Save Changes' : 'Create Lead'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewLeadModal;
